import { AutoComplete, Button, CILabel, Input, Textarea } from 'ccs-ui-components';
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CapsuleButton from '../../../../_components/capsule-button/CapsuleButton';
import { AUTOCOMPLETE_FIELD, SOURCE_REGISTRY_MAXLENGTH, associationEventType } from '../../../../constants';
import { IPatternDefinition, ISourceRegistryValidationModel, SourceRegistryFormData } from '../../../../models';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { fetchServiceEventRequest } from '../../../../store/actions/sagaActions/services/IngressServiceEventSagaActions';
import { addBtnStatus, formatJsonForInput, identifierChecker } from '../../../../utilities/sources/SourceRegistryUtil';
import PatternList from '../pattern-list/PatternList';
import classes from './SourceRegistryForm.module.scss';

interface ISourceRegistryFormProps {
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  formData: ISourceRegistryValidationModel;
  validateSourceRegistryName: (name: string) => void;
  identifierDuplicate: (isDuplicate: string) => void;
  setIdentifiersArray: Dispatch<SetStateAction<string[]>>;
  setPatternList: Dispatch<SetStateAction<IPatternDefinition[]>>;
  identifierInvalidity: boolean;
  view: boolean;
  resIdentifierArray: string[]
  setFormParamValues: Dispatch<SetStateAction<SourceRegistryFormData>>;
  getFormParam: (previousState: ISourceRegistryValidationModel, name: string, currLength: number, maxChars: number, value: string) => ISourceRegistryValidationModel;
  edit: boolean;
  isSRNameLoading: boolean;
  patternList: IPatternDefinition[];
  setDisableCreateButton: (isDisabled: boolean) => void
  inputEventValue: string;
  isCreateButtonDisabled: boolean;
  setInputEventValue: (value: string) => void;
}

const SourceRegistryForm = (props: ISourceRegistryFormProps) => {
  const { formData, handleInputChange, validateSourceRegistryName, isCreateButtonDisabled, identifierDuplicate, setIdentifiersArray, inputEventValue, setInputEventValue, identifierInvalidity, view, setFormParamValues, getFormParam, resIdentifierArray, edit, isSRNameLoading, setPatternList, patternList, setDisableCreateButton } = props;
  const [isDuplicate, setIsDuplicate] = useState('');
  const [identifierErr, setIdentifierErr] = useState('');
  const [isInvalid, setIsInvalid] = useState(true);
  const [eventErrorMsg, setEventErrorMsg] = useState('');
  const [eventOptions, setEventOptions] = useState<string[]>([]);
  const [clearData, setClearData] = useState<boolean>(false);
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const requestSpecValidation = !formData.requestSpecJson.isValid && formData.requestSpecJson.isTouched;
  const SOURCE_EVENT_REGISTRY_ERRORS = {
    duplicateSrcIdentifiers: translate('SERErrors.duplicateSrcIdentifiers')
  };

  const REGISTRY_ERRORS = {
    no_results: translate('registryErrors.no_results')
  };

  const HELPER_TEXT = {
    sourceNameText: translate('fsmHelperTxt.sourceNameText'),
    srcIdnText: translate('fsmHelperTxt.srcIdnText'),
    searchText: translate('fsmHelperTxt.searchText'),
    serJSON: translate('fsmHelperTxt.serJSON'),
    replyTimeout: translate('fsmHelperTxt.replyTimeout'),
    replyToTopicText: translate('fsmHelperTxt.replyToTopicText')
  };

  const { activeServiceEvent, isLoading: isEventLoading } = useSelector(
    (state) => state.fetchIngressServiceEventReducer
  );

  /**
   * @method onAddClickHandler
   * @description Handles the click event when the "Add" button is clicked.
   */
  const onAddClickHandler = () => {
    const itemArray = [...resIdentifierArray];
    itemArray.push(formData.identifiers.value);
    formData.identifiersArray.value = itemArray.toString();
    formData.identifiers.value = '';
    formData.identifiers.isValid = true;
    setIdentifiersArray(itemArray);
    setIsInvalid(true);
  };

  /**
   * @method onDeleteHandler
   * @description Handles the deletion of an item from the resIdentifierArray.
   * @param index - The index of the item to be deleted.
   */
  const onDeleteHandler = (index: number) => {
    const itemArray = [...resIdentifierArray];
    itemArray.splice(index, 1);
    setIdentifiersArray(itemArray);
    formData.identifiersArray.value = itemArray.toString();
    setFormParamValues((previousState: ISourceRegistryValidationModel) => getFormParam(previousState, 'identifiers', formData.identifiers.value.length, 500, formData.identifiers.value));
  };

  /**
   * @method handleEventSelect
   * @description Handles the event selection in the SourceRegistryForm.
   * @param value - The selected value.
   */
  const handleEventSelect = (value: string) => {
    setDisableCreateButton(false);
    const [, , third] = value.split(' ');
    setFormParamValues((prev) => {
      const newFormData = structuredClone(prev);
      newFormData.eventName.value = value;
      newFormData.eventName.isTouched = true;
      newFormData.eventName.version = third ? parseInt(third.split(')')[0], 10) : 1;
      newFormData.eventName.isTouched = true;
      newFormData.eventName.error = '';
      newFormData.eventName.isValid = true;
      return newFormData;
    });
    setInputEventValue(value);
    setEventOptions([]);
  };

  /**
   * @method handleEventChange
   * @description Handles the change event of the input field.
   * @param value - The new value of the input field.
   */
  const handleEventChange = (value: string) => {
    if (value.length) {
      setDisableCreateButton(true);
    } else {
      setDisableCreateButton(false);
    }
    setFormParamValues((prev) => {
      const newFormData = structuredClone(prev);
      if (!value) {
        newFormData.eventName.version = 0;
      }
      newFormData.eventName.value = value;
      newFormData.eventName.isTouched = true;
      return newFormData;
    }); // needed for accurate disabling of Create button
    setInputEventValue(value);
    if (value.length > 2) dispatch(fetchServiceEventRequest(value, 'active', associationEventType.INGRESS));
    if (inputEventValue.length < 1) setClearData(false);
  };

  const getEventClassName = (
    isValid: boolean,
    eventErrorMsg: string,
    isEventLoading: boolean
  ): string => ((isValid || (eventErrorMsg && !isEventLoading))
    ? classes['cp-sr-autocomplete-error']
    : classes['cp-sr-autocomplete-event-relative']);

  useEffect(() => {
    if (Array.isArray(activeServiceEvent) && activeServiceEvent.length > 0) {
      const filteredData = activeServiceEvent.map((item) => `${item.eventName} (Ver ${item.version})`);
      setEventOptions(filteredData);
    }
  }, [activeServiceEvent]);

  // to check if the active event option has records available for search
  useEffect(() => {
    if (formData.eventName.isTouched && formData.eventName.value.length > 2 && !activeServiceEvent.length && inputEventValue.length > 2) {
      setEventOptions([]);
      setEventErrorMsg(REGISTRY_ERRORS.no_results);
    } else {
      setEventErrorMsg('');
    }
  }, [eventErrorMsg, formData.eventName.value, activeServiceEvent, edit]);

  // to toggle status of add btn in identifier input
  useEffect(() => {
    const obj = resIdentifierArray.filter((item) => item === formData.identifiers.value);
    if (obj.length) {
      setIsDuplicate(SOURCE_EVENT_REGISTRY_ERRORS.duplicateSrcIdentifiers);
    } else {
      setIsDuplicate('');
    }
    setIdentifierErr(identifierChecker(formData, isDuplicate));
    setIsInvalid(addBtnStatus(formData, isDuplicate));
  }, [formData, isInvalid, isDuplicate, resIdentifierArray]);

  useEffect(() => {
    identifierDuplicate(isDuplicate);
  }, [isDuplicate]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (formData.sourceRegistryName.value.length && !view && !edit) {
      timer = setTimeout(() => {
        validateSourceRegistryName(formData.sourceRegistryName.value);
      }, 500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [formData.sourceRegistryName.value, view, edit]);

  useEffect(() => {
    if ((formData.eventName.value && formData.eventName.version) && ((view && !isCreateButtonDisabled) || (edit && !formData.eventName.isTouched))) {
      const eventNameValueWithVersion = `${formData.eventName.value} (Ver ${formData.eventName.version})`;
      setInputEventValue(eventNameValueWithVersion);
      setEventErrorMsg('');
    } else if (!formData.eventName.isTouched) {
      setInputEventValue('');
    }
  }, [edit, view, formData.eventName.value, isCreateButtonDisabled]);

  return (
    <div className={`form-wrapper-container ${classes['cp-sr-form-wrapper']}`} data-testid="cp-source-registry-form">
      <div className={classes['cp-sr-form-header']}>
        <p className={classes['cp-sr-header-title']}>
          {translate('source_registry_form.form_title')}
        </p>
        <p className={classes['cp-sr-header-subtitle']}>
          {translate('source_registry_form.form_sub_title')}
        </p>
      </div>
      <Form className={classes['cp-sr-form']} onSubmit={(event) => { event.preventDefault(); }} data-testid="cp-sr-form">
        <Form.Group className={classes['cp-sr-form-grp']}>
          <Input
            type="text"
            placeholder={String(translate('source_registry_form.plchd_source_registry_name'))}
            label=""
            name="sourceRegistryName"
            value={formData.sourceRegistryName.value}
            onChange={handleInputChange}
            maxLength={Number(SOURCE_REGISTRY_MAXLENGTH.sourceRegistryNameMaxLength)}
            error={!formData.sourceRegistryName.isValid}
            data-testid="sr-name-form-input"
            disabled={view || edit}
            isLoading={isSRNameLoading}
            labelText={String(translate('source_registry_form.lbl_source_registry_name'))}
            helperText={HELPER_TEXT.sourceNameText}
            errorTxt={formData.sourceRegistryName.error}
            isMandatory
            infoIconTxt={String(translate('source_registry_form.source_name_info_bubble'))}
          />
        </Form.Group>
        <Form.Group className={classes['cp-sr-form-grp']}>
          {view && (
            <CILabel id="" isMandatory labelText={String(translate('source_registry_form.lbl_identifiers'))} infoIconText={translate('source_registry_form.unique_identifier_info_bubble')} />
          )}
          <div className={classes['cp-sr-identifier-input']}>
            {(!view || edit) && (
              <>
                <Input
                  type="text"
                  placeholder={String(translate('source_registry_form.plchd_identifiers'))}
                  label=""
                  className={classes['cp-sr-form-input']}
                  name="identifiers"
                  value={formData.identifiers.value}
                  onChange={handleInputChange}
                  maxLength={Number.MAX_SAFE_INTEGER}
                  error={identifierInvalidity}
                  data-testid="sr-identifier-form-input"
                  disabled={view}
                  labelText={String(translate('source_registry_form.lbl_identifiers'))}
                  helperText={HELPER_TEXT.srcIdnText}
                  isMandatory
                  errorTxt={identifierErr}
                  infoIconTxt={String(translate('source_registry_form.unique_identifier_info_bubble'))}
                />
                <Button
                  variant="secondary"
                  className={identifierInvalidity ? classes['cp-sr-btn-disabled'] : classes['cp-sr-btn-enabled']}
                  onClick={onAddClickHandler}
                  disabled={isInvalid}
                  data-testid="sr-add-form-btn"
                >
                  {translate('source_registry_form.btn_add')}
                </Button>
              </>
            )}
          </div>
        </Form.Group>
        {resIdentifierArray.map((item, index) => (
          <span key={item} className={classes['cp-sr-identifier-label']}>
            <CapsuleButton
              label={item}
              onClickHandler={() => onDeleteHandler(index)}
              deleteIcon={!view}
              view={view}
              ellipseLength={25}
              edit={edit}
              errEditStateForm={view}
              className={view ? 'sr-view-capsule-btn' : 'sr-capsule-btn'}
            />
          </span>
        ))}
        <Form.Group className={classes['cp-sr-form-grp']}>
          <div className={getEventClassName(!formData.eventName.isValid, eventErrorMsg, isEventLoading)}>
            {(!view || edit) ? (
              <AutoComplete
                suggestions={eventOptions}
                onSelect={handleEventSelect}
                onqueryUpdate={handleEventChange}
                isLoading={isEventLoading}
                placeHolder={String(translate('source_event_registry_form.plchd_event_name'))}
                clearData={clearData}
                minSearchLength={3}
                defaultValue={inputEventValue}
                labelText={String(translate('source_event_registry_form.lbl_event_name'))}
                helperText={HELPER_TEXT.searchText}
                error={(eventErrorMsg.length > 0 && !isEventLoading) || !formData.eventName.isValid}
                errorTxt={(eventErrorMsg && !isEventLoading) ? eventErrorMsg : formData.eventName.error}
                fieldName={AUTOCOMPLETE_FIELD.eventName}
                infoIconTxt={String(translate('source_event_registry_form.event_name_info_bubble'))}
              />
            ) : <Input
              label=""
              name=""
              type="text"
              placeholder={String(translate('SERForm.eventName'))}
              value={inputEventValue}
              disabled={view}
              data-testid="sr-src-intent-disabled-form-input"
              labelText={String(translate('source_event_registry_form.lbl_event_name'))}
              helperText={HELPER_TEXT.searchText}
              infoIconTxt={String(translate('source_event_registry_form.event_name_info_bubble'))}
            />}
          </div>
        </Form.Group>
        <Form.Group className={classes['cp-sr-form-grp']}>
          <Textarea
            type="text"
            placeholder={
              String(translate('source_event_registry_form.lbl_placeholder_config', {
                replace: { key: '"key"', value: '"value"' }
              }))
            }
            label=""
            name="requestSpecJson"
            value={formatJsonForInput(formData.requestSpecJson.value)}
            onChange={handleInputChange}
            error={requestSpecValidation}
            maxLength={Number.MAX_SAFE_INTEGER}
            disabled={view}
            data-testid="sr-config-form-input"
            labelText={String(translate('source_registry_form.lbl_req_spec_json'))}
            errorTxt={formData.requestSpecJson.error}
            helperText={HELPER_TEXT.serJSON}
            infoIconTxt={String(translate('source_event_registry_form.json_transformation_info_bubble'))}
          />
        </Form.Group>
        <Form.Group className={classes['cp-sr-form-grp']}>
          <Input
            type="text"
            placeholder={String(translate('source_topic_registry_form.plchd_reply_topic_name'))}
            label=""
            name="replyToTopic"
            value={formData.replyToTopic.value}
            onChange={handleInputChange}
            maxLength={101}
            error={!formData.replyToTopic.isValid}
            data-testid="sr-replytotopic-form-input"
            disabled={view}
            labelText={translate('source_topic_registry_form.lbl_reply_topic_name') ?? ''}
            helperText={HELPER_TEXT.replyToTopicText}
            errorTxt={formData.replyToTopic.error}
            infoIconTxt={HELPER_TEXT.replyToTopicText}
          />
        </Form.Group>
        <Form.Group className={classes['cp-sr-form-grp']}>
          <Input
            type="text"
            placeholder={String(translate('source_topic_registry_form.plchd_reply_timeout'))}
            label=""
            className={classes['cp-sr-form-input']}
            name="replyTimeOut"
            value={formData.replyTimeOut.value}
            onChange={handleInputChange}
            maxLength={Number.MAX_SAFE_INTEGER}
            error={!formData.replyTimeOut.isValid}
            data-testid="sr-reply-timeout-form-input"
            disabled={view}
            labelText={String(translate('source_topic_registry_form.lbl_reply_timeout'))}
            helperText={HELPER_TEXT.replyTimeout}
            errorTxt={formData.replyTimeOut.error}
            infoIconTxt={String(translate('source_topic_registry_form.reply_timeout_info_bubble'))}
          />
        </Form.Group>
      </Form>
      <PatternList view={view} edit={edit} setPatternList={setPatternList} patternList={patternList} />
    </div>
  );
};
export default SourceRegistryForm;
